// ============================================================
// ComposeChrome 뷰별 오버라이드 해소 (순수 함수, React 무관)
// ============================================================
//
// ComposeChromeConfig.items 각 항목의 `responsive?.[activeViewKey]` 델타(hidden/slot/order)를
// base(데스크톱) 값 위에 해소해 활성 뷰의 header/actionBar 항목을 산출한다.
// 결정: docs/decisions/2026-07-09-composer-responsive-view-overrides.md §3 (chrome override 대상=hidden/slot/order)
//
// - activeViewKey === base(desktop)이거나 해당 뷰 델타 없으면 base 값 순수 상속.
// - hidden=true 항목은 제외. 남은 항목을 effectiveSlot으로 분류 + effectiveOrder 오름차순 정렬.
// - 반환 항목은 slot/order만 해소값으로 override한 얕은 복사(id/actionId/link/label/primary/responsive 보존).

import { COMPOSER_BASE_VIEW_KEY } from '@safetyhub/shared';
import type {
  ComposeChromeConfig,
  ComposeChromeDisplay,
  ComposeChromeItem,
  ComposeMode,
} from '@safetyhub/shared';

/**
 * 활성 뷰에 맞춰 chrome 항목을 해소해 header/actionBar 슬롯별로 분류·정렬한다.
 *
 * @param config       작성 폼 chrome 구성(base 직접값 + responsive 델타).
 * @param activeViewKey 활성 뷰키(COMPOSER_VIEWS.key). base(desktop)면 델타 무시.
 * @param mode         작성 모드(create/edit). 항목 visibleIn과 불일치하면 제외. 기본 'create'.
 */
export function resolveComposeChromeItems(
  config: ComposeChromeConfig,
  activeViewKey: string,
  mode: ComposeMode = 'create',
): { headerItems: ComposeChromeItem[]; actionBarItems: ComposeChromeItem[] } {
  const isBaseView = !activeViewKey || activeViewKey === COMPOSER_BASE_VIEW_KEY;

  const headerItems: ComposeChromeItem[] = [];
  const actionBarItems: ComposeChromeItem[] = [];

  for (const item of config.items) {
    // 모드 축: visibleIn 지정 + 현재 모드와 불일치면 제외(미지정=공통, 양 모드 노출).
    if (item.visibleIn != null && item.visibleIn !== mode) continue;

    // base 뷰이거나 해당 뷰 델타 없으면 순수 상속(델타 undefined).
    const ov = isBaseView ? undefined : item.responsive?.[activeViewKey];

    if (ov?.hidden) continue; // 이 뷰에서 숨김.

    const effectiveSlot = ov?.slot ?? item.slot;
    const effectiveOrder = ov?.order ?? item.order;

    // 델타가 slot/order를 바꾼 경우만 얕은 복사로 반영. 그 외는 원본 참조 유지.
    const resolved =
      effectiveSlot === item.slot && effectiveOrder === item.order
        ? item
        : { ...item, slot: effectiveSlot, order: effectiveOrder };

    if (effectiveSlot === 'header') headerItems.push(resolved);
    else actionBarItems.push(resolved);
  }

  headerItems.sort((a, b) => a.order - b.order);
  actionBarItems.sort((a, b) => a.order - b.order);

  return { headerItems, actionBarItems };
}

/**
 * chrome 항목의 활성 뷰 표시형(label/icon/iconLabel)을 해소한다.
 *
 * 우선순위: ① 항목의 그 뷰 명시 오버라이드(responsive[view].display) 최우선. ② 그 외 'label'.
 * 기본은 모든 뷰에서 텍스트 라벨 — 라벨이 있으면 아이콘은 중복(2026-07-10 사용자 확정,
 * 내장 액션 라벨이 모두 짧아 compact 뷰에도 들어감). compact 뷰는 표시형이 아니라
 * 패딩 압축(셸)으로 대응하고, 아이콘(icon/iconLabel)은 admin 뷰별 오버라이드 옵션으로만 제공.
 */
export function resolveChromeItemDisplay(
  item: ComposeChromeItem,
  activeViewKey: string,
): ComposeChromeDisplay {
  return item.responsive?.[activeViewKey]?.display ?? 'label';
}
